// Saft: små DOM-effekter oven på kortet, der skal have bogstaver eller flyve hen til HUD'en.
// Mønter fra byerne til kassen, "PUH!" og "LYN! +3" over en by, og tankebobler over kunder, der
// venter på strøm. Alt er pynt: aria-hidden, ingen pointer-events, og teksten findes også et
// andet sted (kassen, toasten eller skærmlæserens besked). Rolig bevægelse: ingen flyvende mønter.

import type { State } from '../sim';
import { TICK_HZ } from '../sim';

type P = { x: number; y: number };
type Box = { x: number; y: number; w: number; h: number };
type Where = () => P | null;

/** Højst så mange mønter i luften ad gangen. */
export const MAX_COINS = 3;
/** Mønternes flyvetid. */
export const COIN_MS = 450;
/** Tankebobler: efter så længe uden ledning, og højst så mange. */
export const THOUGHT_AFTER_TICKS = 3 * TICK_HZ;
export const MAX_THOUGHTS = 3;
const THOUGHT_TEXT = ['Hallo?', 'Strøm, tak!', 'Vi venter her'];
const THOUGHT_SWAP_MS = 2600;

interface Pinned {
  el: HTMLElement;
  at: Where;
  until: number;
  dy: number;
}

/** Kunder, der har ventet over 3 s uden en eneste ledning: nyeste først, højst tre. */
export function waitingCustomers(state: State): string[] {
  const wired = new Set<string>();
  for (const l of state.lines) {
    wired.add(l.a);
    wired.add(l.b);
  }
  return state.nodes
    .filter((n) => n.active && !n.phantom && n.city && !n.city.out && !wired.has(n.id) && state.tick - n.activeSince >= THOUGHT_AFTER_TICKS)
    .sort((a, b) => b.activeSince - a.activeSince || (a.id < b.id ? -1 : 1))
    .slice(0, MAX_THOUGHTS)
    .map((n) => n.id);
}

export class Juice {
  readonly el = document.createElement('div');
  private coinsUp = 0;
  private pinned: Pinned[] = [];
  private thoughts = new Map<string, { el: HTMLElement; i: number; at: number }>();
  private thoughtN = 0;

  constructor() {
    this.el.className = 'juice';
    this.el.setAttribute('aria-hidden', 'true');
  }

  /** Mønter fra punkterne til målet (kassens mønt). Returnerer antal, der fløj. */
  coins(from: P[], to: P): number {
    let n = 0;
    for (const p of from) {
      if (this.coinsUp >= MAX_COINS) break;
      const c = document.createElement('span');
      c.className = 'juice-coin';
      c.style.left = `${Math.round(p.x)}px`;
      c.style.top = `${Math.round(p.y)}px`;
      this.el.append(c);
      this.coinsUp++;
      n++;
      const dx = to.x - p.x;
      const dy = to.y - p.y;
      const delay = (n - 1) * 70;
      const done = (): void => {
        c.remove();
        this.coinsUp--;
      };
      if (typeof c.animate !== 'function') {
        done();
        continue;
      }
      // En lille bue: mønten springer op med en ud-kurve (svarer med det samme), og suges så ind
      // i kassen med en ind-kurve, så den lander med fart. Kurven står pr. stykke, ikke samlet.
      const a = c.animate(
        [
          { transform: 'translate(-50%, -50%) scale(0.85)', opacity: 0, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' },
          { transform: `translate(calc(-50% + ${dx * 0.2}px), calc(-50% + ${dy * 0.2 - 26}px)) scale(1)`, opacity: 1, offset: 0.32, easing: 'cubic-bezier(0.55, 0, 0.75, 0.3)' },
          { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.55)`, opacity: 0.9 },
        ],
        { duration: COIN_MS, delay, easing: 'linear', fill: 'backwards' },
      );
      a.onfinish = done;
      a.oncancel = done;
    }
    return n;
  }

  /** En boble over en by, der følger kortet: "PUH!" eller "LYN! +3". */
  pop(at: Where, text: string, kind: 'puh' | 'lyn', ms: number, dy = 58): void {
    // Samme boble over samme by tæller én gang (online kom der 3-6 PUH! oven i hinanden).
    const p0 = at();
    const now = performance.now();
    const twin = this.pinned.some((p) => {
      if (p.el.textContent !== text || p.until - now < ms / 2) return false;
      const q = p.at();
      return !!p0 && !!q && Math.abs(q.x - p0.x) < 8 && Math.abs(q.y - p0.y) < 8;
    });
    if (twin) return;
    const el = document.createElement('span');
    el.className = `juice-pop ${kind}`;
    el.textContent = text;
    el.style.setProperty('--ms', `${ms}ms`);
    this.el.append(el);
    this.pinned.push({ el, at, until: performance.now() + ms, dy });
    this.place(this.pinned[this.pinned.length - 1] as Pinned);
  }

  private place(p: Pinned): void {
    const at = p.at();
    if (!at) {
      p.el.style.visibility = 'hidden';
      return;
    }
    p.el.style.visibility = '';
    p.el.style.left = `${Math.round(at.x)}px`;
    p.el.style.top = `${Math.round(at.y - p.dy)}px`;
  }

  /** Hver frame: bobler følger kortet, tankebobler skifter tekst. `show` er falsk under skærme. */
  frame(now: number, show: boolean, waiting: string[], where: (id: string) => P | null, k: number, avoid: readonly Box[] = []): void {
    this.el.hidden = !show;
    this.pinned = this.pinned.filter((p) => {
      if (now >= p.until) {
        p.el.remove();
        return false;
      }
      this.place(p);
      return true;
    });
    const want = show ? waiting : [];
    for (const [id, t] of this.thoughts) {
      if (want.includes(id)) continue;
      t.el.remove();
      this.thoughts.delete(id);
    }
    for (const id of want) {
      let t = this.thoughts.get(id);
      if (!t) {
        const el = document.createElement('span');
        el.className = 'juice-think';
        // Hver by starter et nyt sted i rækken, så tre byer ikke siger det samme.
        const i = this.thoughtN++ % THOUGHT_TEXT.length;
        el.textContent = THOUGHT_TEXT[i] ?? '';
        this.el.append(el);
        t = { el, i, at: now };
        this.thoughts.set(id, t);
      } else if (now - t.at > THOUGHT_SWAP_MS) {
        t.i = (t.i + 1) % THOUGHT_TEXT.length;
        t.at = now;
        t.el.textContent = THOUGHT_TEXT[t.i] ?? '';
      }
      const p = where(id);
      if (!p) {
        t.el.style.visibility = 'hidden';
        continue;
      }
      // Inden for skærmen: en by ude ved kanten fik sin boble skåret af (playtest runde 2).
      const bw = t.el.offsetWidth;
      const bh = t.el.offsetHeight;
      const y = Math.max(bh + 8, p.y - 40 * k);
      // left er boblens midte og top dens bund (translate -50 %, -100 %).
      const fit = (cx: number) => Math.max(bw / 2 + 8, Math.min(window.innerWidth - bw / 2 - 8, cx));
      // Højre for byen, ellers venstre. Står "Træk!"-boblen begge steder, tier byen (break-ui 320):
      // boblen peger allerede på den.
      const xs = [fit(p.x + 16 * k), fit(p.x - 16 * k - bw / 2)];
      const x = xs.find((cx) => !avoid.some((a) => cx - bw / 2 < a.x + a.w && cx + bw / 2 > a.x && y - bh < a.y + a.h && y > a.y));
      t.el.style.visibility = x === undefined ? 'hidden' : '';
      t.el.style.left = `${Math.round(x ?? xs[0] ?? 0)}px`;
      t.el.style.top = `${Math.round(y)}px`;
    }
  }

  /** Skærmrektangler for boblerne, så navneskilte og hånden kan gå uden om dem. */
  rects(): Box[] {
    const out: Box[] = [];
    if (this.el.hidden) return out;
    for (const el of this.el.querySelectorAll<HTMLElement>('.juice-think, .juice-pop')) {
      if (el.style.visibility === 'hidden') continue;
      const r = el.getBoundingClientRect();
      if (r.width > 0) out.push({ x: r.left, y: r.top, w: r.width, h: r.height });
    }
    return out;
  }

  reset(): void {
    for (const p of this.pinned) p.el.remove();
    this.pinned = [];
    for (const t of this.thoughts.values()) t.el.remove();
    this.thoughts.clear();
  }
}
